import { template as _$template } from "solid-js/web";
import { setStyleProperty as _$setStyleProperty } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div class=detail-pane>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<p class=detail-pane__empty>Select an asset to inspect it.`),
  _tmpl$3 = /*#__PURE__*/_$template(`<header class=detail-pane__header><h3 class=detail-pane__title></h3><dl class=detail-pane__meta><div class=detail-pane__field><dt>Source</dt><dd></dd></div><div class="detail-pane__field detail-pane__field--hash"><dt>SHA-256</dt><dd>`),
  _tmpl$4 = /*#__PURE__*/_$template(`<p class=detail-pane__empty>No viewer for “<!>” assets yet.`),
  _tmpl$5 = /*#__PURE__*/_$template(`<div class=detail-pane__viewer>`),
  _tmpl$6 = /*#__PURE__*/_$template(`<div class=detail-pane__field><dt></dt><dd>`),
  _tmpl$7 = /*#__PURE__*/_$template(`<div class=detail-pane__viewer-pane>`);
import { createEffect, createSignal, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { summarizeMeta } from "./meta-summary.ts";
import { AudioViewer } from "./viewers/AudioViewer.tsx";
import { FontViewer } from "./viewers/FontViewer.tsx";
import { ModelViewer } from "./viewers/ModelViewer.tsx";
import { PaletteViewer } from "./viewers/PaletteViewer.tsx";
import { ScreenViewer } from "./viewers/ScreenViewer.tsx";
import { SpriteViewer } from "./viewers/SpriteViewer.tsx";
import { WorldViewer } from "./viewers/WorldViewer.tsx";

/**
 * Partial on purpose: `ASSET_CATEGORIES` grows as bake stages land, and a
 * category whose viewer hasn't been written yet should show a note rather
 * than break the build or render a blank pane.
 */
const VIEWERS = {
  palettes: PaletteViewer,
  screens: ScreenViewer,
  sprites: SpriteViewer,
  fonts: FontViewer,
  models: ModelViewer,
  music: AudioViewer,
  sfx: AudioViewer,
  world: WorldViewer
};
const CATEGORIES = Object.keys(VIEWERS);

/**
 * Selected-asset detail pane: source path, sha256, category-specific summary
 * fields, then the matching viewer.
 *
 * **Every category's viewer mounts once and stays mounted** (structural fix
 * for the GPU device DetailPane used to retain per category switch, roadmap
 * item 14). The previous shape picked one viewer with `<Dynamic
 * component={VIEWERS[entry().category]} .../>`, which fully unmounts the
 * outgoing component and mounts a fresh one on every category switch —
 * `ModelViewer`/`WorldViewer` each own a `three.js` `WebGPURenderer`
 * (`three-model-scene.ts`), and a full unmount-then-remount cycle measured
 * at ~4.4 MB of renderer-process RSS retained per switch that never came
 * back (see `docs/engineering/lessons.md`'s "world-stability lane" entry for
 * the same class of WebGPU cleanup gap in a different scene). Below, each
 * category's pane is created once — lazily, the first time that category is
 * ever selected, via `lastByCategory` — and never unmounted afterward;
 * switching categories only toggles `display: none` (`.detail-pane__viewer-pane`
 * in app.css), which leaves the inactive panes' renderers alive and idle
 * rather than disposed and recreated.
 */
export function DetailPane(props) {
  // Remembers the most recently selected entry per category, so a pane that
  // is hidden (not unmounted) resumes on its own last selection rather than
  // going blank when the user switches back to it.
  const [lastByCategory, setLastByCategory] = createSignal({});
  createEffect(() => {
    const entry = props.entry;
    if (entry) setLastByCategory(prev => prev[entry.category] === entry ? prev : {
      ...prev,
      [entry.category]: entry
    });
  });
  return (() => {
    var _el$ = _tmpl$();
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return props.entry;
      },
      get fallback() {
        return _tmpl$2();
      },
      children: entry => [(() => {
        var _el$3 = _tmpl$3(),
          _el$4 = _el$3.firstChild,
          _el$5 = _el$4.nextSibling,
          _el$6 = _el$5.firstChild,
          _el$7 = _el$6.firstChild,
          _el$8 = _el$7.nextSibling,
          _el$9 = _el$6.nextSibling,
          _el$0 = _el$9.firstChild,
          _el$1 = _el$0.nextSibling;
        _$insert(_el$4, () => entry().id);
        _$insert(_el$8, () => entry().sourcePath);
        _$insert(_el$5, _$createComponent(For, {
          get each() {
            return summarizeMeta(entry().category, entry().meta);
          },
          children: field => (() => {
            var _el$15 = _tmpl$6(),
              _el$16 = _el$15.firstChild,
              _el$17 = _el$16.nextSibling;
            _$insert(_el$16, () => field.label);
            _$insert(_el$17, () => field.value);
            return _el$15;
          })()
        }), _el$9);
        _$insert(_el$1, () => entry().sha256);
        return _el$3;
      })(), (() => {
        var _el$10 = _tmpl$5();
        _$insert(_el$10, _$createComponent(For, {
          each: CATEGORIES,
          children: category => // Gates *first* mount on that category having a selection at all — once
          // truthy, `lastByCategory()[category]` never reverts to falsy, so this
          // `Show` never unmounts the pane again; only the sibling `style` below does
          // the actual show/hide.
          _$createComponent(Show, {
            get when() {
              return lastByCategory()[category];
            },
            children: categoryEntry => (() => {
              var _el$18 = _tmpl$7();
              _$insert(_el$18, _$createComponent(Dynamic, {
                get component() {
                  return VIEWERS[category];
                },
                get entry() {
                  return categoryEntry();
                }
              }));
              _$effect(_$p => _$setStyleProperty(_el$18, "display", entry().category === category ? "" : "none"));
              return _el$18;
            })()
          })
        }), null);
        _$insert(_el$10, _$createComponent(Show, {
          get when() {
            return !VIEWERS[entry().category];
          },
          get children() {
            var _el$11 = _tmpl$4(),
              _el$12 = _el$11.firstChild,
              _el$14 = _el$12.nextSibling,
              _el$13 = _el$14.nextSibling;
            _$insert(_el$11, () => entry().category, _el$14);
            return _el$11;
          }
        }), null);
        return _el$10;
      })()]
    }));
    return _el$;
  })();
}